import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Loader2, User } from 'lucide-react';
import { cn, formatDate, formatPhone } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { searchPatients } from '@/services/patients.service';
import type { PatientSummary } from '@/types/patient.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PatientSearchInputProps {
  onSelect: (patient: PatientSummary | null) => void;
  selectedPatient?: PatientSummary | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /**
   * If set, this patient ID is filtered out of the result list. Used by the
   * patient-merge modal to hide the primary patient from the duplicate-pick
   * list, matching legacy dt-full-patient-list.php line 209.
   */
  excludePatientId?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDob(dob: string | null): string {
  const formattedDob = formatDate(dob);
  return formattedDob === '—' ? 'DOB unknown' : `DOB ${formattedDob}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PatientSearchInput({
  onSelect,
  selectedPatient,
  placeholder = 'Search patients…',
  disabled = false,
  className,
  excludePatientId,
}: PatientSearchInputProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PatientSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep dropdown anchored to input via fixed positioning so it escapes overflow clipping
  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateDropdownPosition();
  }, [open, results, updateDropdownPosition]);

  // ─── Fetch results ───────────────────────────────────────────────────

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!q || selectedPatient) {
      setResults([]);
      setOpen(false);
      // A request cancelled mid-flight skips its own finally, so this branch owns
      // the reset — selecting a patient while one is in flight would otherwise
      // leave the spinner running for good.
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchResults() {
      setLoading(true);
      try {
        const data = await searchPatients(q);
        if (!cancelled) {
          const filtered = excludePatientId
            ? data.filter((p) => p.patientId !== excludePatientId)
            : data;
          setResults(filtered);
          setOpen(filtered.length > 0);
          setActiveIndex(-1);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchResults();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, selectedPatient, excludePatientId]);

  // ─── Close on outside click; reposition on scroll ───────────────────

  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function handleScroll(e: Event) {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      updateDropdownPosition();
    }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [updateDropdownPosition]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleSelect = useCallback(
    (patient: PatientSummary) => {
      onSelect(patient);
      setQuery('');
      setOpen(false);
      setResults([]);
    },
    [onSelect],
  );

  const handleClear = useCallback(() => {
    onSelect(null);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  }, [onSelect]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (activeIndex >= 0 && results[activeIndex]) {
          e.preventDefault();
          handleSelect(results[activeIndex]);
        }
        break;
      case 'Escape':
        // Contain Escape to the dropdown (this handler already returned early
        // when closed) so it can't reach an enclosing Modal and close it.
        e.stopPropagation();
        setOpen(false);
        break;
    }
  }

  // ─── Display selected patient ─────────────────────────────────────────

  if (selectedPatient) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2',
          className,
        )}
      >
        <User className="h-4 w-4 flex-shrink-0 text-slate-400" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-slate-900">{selectedPatient.displayName}</span>
          <span className="ml-2 text-xs text-slate-500">{formatDob(selectedPatient.dateOfBirth)}</span>
        </div>
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear patient selection"
          className="flex-shrink-0 text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          onClick={() => results.length > 0 && !open && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          className={cn(
            'w-full rounded-md border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm text-slate-900',
            'placeholder:text-slate-400',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          )}
        />
        {loading && (
          /* `animate-spin` overwrites `transform`, so center with margin-auto rather
             than -translate-y-1/2, which the animation drops (drifting the icon down). */
          <Loader2 className="absolute right-3 inset-y-0 my-auto h-4 w-4 text-slate-400 animate-spin" />
        )}
      </div>

      {/* Dropdown — portalled to document.body to escape modal stacking contexts */}
      {open && results.length > 0 && ReactDOM.createPortal(
        <ul
          ref={dropdownRef}
          role="listbox"
          aria-label="Patient search results"
          style={dropdownStyle}
          className="rounded-md border border-slate-200 bg-white shadow-lg py-1 max-h-60 overflow-y-auto"
        >
          {results.map((patient, index) => (
            <li
              key={patient.patientId}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(patient);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn(
                'flex items-center gap-3 px-3 py-2 cursor-pointer',
                index === activeIndex ? 'bg-blue-50' : 'hover:bg-slate-50',
              )}
            >
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-100">
                <User className="h-3.5 w-3.5 text-slate-500" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {patient.displayName}
                  {patient.displayId ? (
                    <span className="ml-2 font-mono text-xs font-normal text-slate-500">
                      #{patient.displayId}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-slate-500">
                  {formatDob(patient.dateOfBirth)}
                  {patient.cellPhone ? (
                    <span className="ml-2">{formatPhone(patient.cellPhone)}</span>
                  ) : null}
                </p>
                {patient.email && (
                  <p className="text-xs text-slate-400 truncate">{patient.email}</p>
                )}
              </div>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
